import React, { useEffect, useState } from 'react';
import { CheckCircle, Clock, X, ChevronLeft, Home, CreditCard, Sparkles, Calendar, MousePointer, Check, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
interface PianificaPuliziaWizardProps {
  isOpen: boolean;
  onClose: () => void;
}
interface AiSuggestion {
  id: string;
  date: string;
  dayName: string;
  dayNumber: string;
  month: string;
  time: string;
  checkOut: string;
  checkIn: string;
  type: 'standard' | 'express';
  confidence: number;
  reason: string;
  selected: boolean;
}
export function PianificaPuliziaWizard({
  isOpen,
  onClose
}: PianificaPuliziaWizardProps) {
  const [wizardMode, setWizardMode] = useState<'choice' | 'manual' | 'ai'>('choice');
  const [wizardStep, setWizardStep] = useState(0); // 0 = choice, 1-4 = manual steps, or AI steps
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  // AI mode states
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState<AiSuggestion[]>([]);
  const [analysisComplete, setAnalysisComplete] = useState(false);
  // Mock AI suggestions
  const mockAiSuggestions: AiSuggestion[] = [{
    id: 'ai-1',
    date: '2024-01-22',
    dayName: 'Lun',
    dayNumber: '22',
    month: 'Gen',
    time: '11:00 - 14:00',
    checkOut: '10:00',
    checkIn: '15:00',
    type: 'standard',
    confidence: 95,
    reason: 'Finestra ottimale tra check-out e check-in',
    selected: false
  }, {
    id: 'ai-2',
    date: '2024-01-24',
    dayName: 'Mer',
    dayNumber: '24',
    month: 'Gen',
    time: '12:00 - 14:30',
    checkOut: '11:00',
    checkIn: '15:00',
    type: 'express',
    confidence: 88,
    reason: 'Finestra stretta, consigliata pulizia express',
    selected: false
  }, {
    id: 'ai-3',
    date: '2024-01-26',
    dayName: 'Ven',
    dayNumber: '26',
    month: 'Gen',
    time: '10:00 - 14:00',
    checkOut: '09:00',
    checkIn: '15:00',
    type: 'standard',
    confidence: 92,
    reason: 'Ampia finestra disponibile',
    selected: false
  }, {
    id: 'ai-4',
    date: '2024-01-28',
    dayName: 'Dom',
    dayNumber: '28',
    month: 'Gen',
    time: '11:00 - 15:00',
    checkOut: '10:00',
    checkIn: '16:00',
    type: 'standard',
    confidence: 90,
    reason: 'Dopo 3 soggiorni, consigliata pulizia approfondita',
    selected: false
  }];
  // Available dates for the wizard
  const availableDates = [{
    date: '2024-01-22',
    dayName: 'Lun',
    dayNumber: '22',
    month: 'Gen'
  }, {
    date: '2024-01-23',
    dayName: 'Mar',
    dayNumber: '23',
    month: 'Gen'
  }, {
    date: '2024-01-24',
    dayName: 'Mer',
    dayNumber: '24',
    month: 'Gen'
  }, {
    date: '2024-01-25',
    dayName: 'Gio',
    dayNumber: '25',
    month: 'Gen'
  }, {
    date: '2024-01-26',
    dayName: 'Ven',
    dayNumber: '26',
    month: 'Gen'
  }, {
    date: '2024-01-27',
    dayName: 'Sab',
    dayNumber: '27',
    month: 'Gen'
  }, {
    date: '2024-01-28',
    dayName: 'Dom',
    dayNumber: '28',
    month: 'Gen'
  }];
  // Available time slots
  const availableTimeSlots = [{
    id: '1',
    time: '09:00 - 12:00',
    available: true
  }, {
    id: '2',
    time: '10:00 - 13:00',
    available: true
  }, {
    id: '3',
    time: '11:00 - 14:00',
    available: false
  }, {
    id: '4',
    time: '12:00 - 15:00',
    available: true
  }, {
    id: '5',
    time: '14:00 - 17:00',
    available: true
  }, {
    id: '6',
    time: '15:00 - 18:00',
    available: true
  }];
  // Cleaning types
  const cleaningTypes = [{
    id: 'standard',
    name: 'Pulizia Standard',
    credits: 1,
    duration: '3 ore',
    description: 'Pulizia completa di tutti gli ambienti'
  }, {
    id: 'deep',
    name: 'Deep Cleaning',
    credits: 2,
    duration: '5 ore',
    description: 'Pulizia profonda con sanificazione'
  }, {
    id: 'express',
    name: 'Pulizia Express',
    credits: 1,
    duration: '2 ore',
    description: 'Pulizia rapida per turnover veloci'
  }];
  const resetWizard = () => {
    setWizardMode('choice');
    setWizardStep(0);
    setSelectedDate(null);
    setSelectedTime(null);
    setSelectedType(null);
    setIsConfirming(false);
    setIsAnalyzing(false);
    setAiSuggestions([]);
    setAnalysisComplete(false);
  };
  const handleClose = () => {
    onClose();
    setTimeout(resetWizard, 300);
  };
  const handleSelectManual = () => {
    setWizardMode('manual');
    setWizardStep(1);
  };
  const handleSelectAi = () => {
    setWizardMode('ai');
    setWizardStep(1);
    setIsAnalyzing(true);
    // Simulate AI analysis
    setTimeout(() => {
      setIsAnalyzing(false);
      setAiSuggestions(mockAiSuggestions);
      setAnalysisComplete(true);
    }, 2500);
  };
  const toggleAiSuggestion = (id: string) => {
    setAiSuggestions(prev => prev.map(s => s.id === id ? {
      ...s,
      selected: !s.selected
    } : s));
  };
  const getSelectedAiSuggestions = () => {
    return aiSuggestions.filter(s => s.selected);
  };
  const getTotalCredits = () => {
    return getSelectedAiSuggestions().reduce((acc, s) => {
      return acc + (s.type === 'standard' ? 1 : 1);
    }, 0);
  };
  const handleConfirm = () => {
    setIsConfirming(true);
    setTimeout(() => {
      if (wizardMode === 'manual') {
        setWizardStep(4);
      } else {
        setWizardStep(3); // AI success step
      }
      setIsConfirming(false);
    }, 1500);
  };
  const handleBack = () => {
    if (wizardMode === 'choice' || wizardStep === 0) {
      return;
    }
    if (wizardStep === 1) {
      setWizardMode('choice');
      setWizardStep(0);
      setIsAnalyzing(false);
      setAnalysisComplete(false);
      setAiSuggestions([]);
    } else {
      setWizardStep(wizardStep - 1);
    }
  };
  const getSelectedDateInfo = () => {
    return availableDates.find(d => d.date === selectedDate);
  };
  const getSelectedTypeInfo = () => {
    return cleaningTypes.find(t => t.id === selectedType);
  };
  const getStepCount = () => {
    if (wizardMode === 'manual') return 3;
    if (wizardMode === 'ai') return 2;
    return 0;
  };
  const getHeaderTitle = () => {
    if (wizardMode === 'choice') return 'Pianifica Pulizia';
    if (wizardMode === 'ai') {
      if (wizardStep === 3) return 'Pulizie Pianificate!';
      return 'Pianificazione AI';
    }
    if (wizardStep === 4) return 'Pulizia Pianificata!';
    return 'Pianifica Pulizia';
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200" onClick={handleClose} data-id="element-595">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col animate-in zoom-in-95 duration-300" onClick={e => e.stopPropagation()} data-id="element-596">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-4 lg:p-6 border-b border-gray-200" data-id="element-597">
          <div className="flex items-center gap-3" data-id="element-598">
            {wizardStep > 0 && wizardStep < (wizardMode === 'manual' ? 4 : 3) && <button onClick={handleBack} className="p-2 hover:bg-gray-100 rounded-lg transition-colors -ml-2" data-id="element-599">
                  <ChevronLeft size={20} className="text-gray-500" data-id="element-600" />
                </button>}
            <div data-id="element-601">
              <h2 className="text-lg font-bold text-navy" data-id="element-602">
                {getHeaderTitle()}
              </h2>
              {wizardMode !== 'choice' && wizardStep > 0 && wizardStep < (wizardMode === 'manual' ? 4 : 3) && <p className="text-xs text-gray-500 mt-0.5" data-id="element-603">
                    Passo {wizardStep} di {getStepCount()}
                  </p>}
            </div>
          </div>
          <button onClick={handleClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-604">
            <X size={20} className="text-gray-500" data-id="element-605" />
          </button>
        </div>

        {/* Progress Bar */}
        {wizardMode !== 'choice' && wizardStep > 0 && wizardStep < (wizardMode === 'manual' ? 4 : 3) && <div className="px-4 lg:px-6 pt-4" data-id="element-606">
              <div className="flex gap-2" data-id="element-607">
                {Array.from({
            length: getStepCount()
          }, (_, i) => i + 1).map(step => <div key={step} className={`h-1 flex-1 rounded-full transition-colors ${step <= wizardStep ? 'bg-teal-deep' : 'bg-gray-200'}`} data-id="element-608" />)}
              </div>
            </div>}

        {/* Modal Content */}
        <div className="flex-1 overflow-y-auto p-4 lg:p-6" data-id="element-609">
          {/* Step 0: Choose Mode */}
          {wizardMode === 'choice' && <div className="space-y-4" data-id="element-610">
              <div data-id="element-611">
                <h3 className="text-sm font-semibold text-navy mb-1" data-id="element-612">
                  Come vuoi pianificare?
                </h3>
                <p className="text-xs text-gray-500" data-id="element-613">
                  Scegli la modalità di pianificazione
                </p>
              </div>

              <div className="space-y-3" data-id="element-614">
                {/* AI Option */}
                <button onClick={handleSelectAi} className="w-full text-left p-4 rounded-xl border-2 border-gray-200 hover:border-teal-deep hover:bg-teal-50/50 transition-all group" data-id="element-615">
                  <div className="flex items-start gap-4" data-id="element-616">
                    <div className="w-12 h-12 rounded-xl bg-coral flex items-center justify-center flex-shrink-0" data-id="element-617">
                      <Sparkles size={24} className="text-white" data-id="element-618" />
                    </div>
                    <div className="flex-1" data-id="element-619">
                      <div className="flex items-center gap-2" data-id="element-620">
                        <span className="text-sm font-semibold text-navy group-hover:text-teal-deep transition-colors" data-id="element-621">
                          Pianificazione AI
                        </span>
                        <span className="text-[10px] font-medium px-2 py-0.5 bg-teal-100 text-coral rounded-full" data-id="element-622">
                          Consigliato
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-623">
                        L'AI analizza i tuoi check-in e check-out e ti propone
                        gli slot migliori per le pulizie
                      </p>
                      <div className="flex items-center gap-3 mt-2" data-id="element-624">
                        <span className="text-[10px] text-gray-400 flex items-center gap-1" data-id="element-625">
                          <CheckCircle size={10} className="text-teal-deep" data-id="element-626" />
                          Analisi automatica
                        </span>
                        <span className="text-[10px] text-gray-400 flex items-center gap-1" data-id="element-627">
                          <CheckCircle size={10} className="text-teal-deep" data-id="element-628" />
                          Più slot in una volta
                        </span>
                      </div>
                    </div>
                  </div>
                </button>

                {/* Manual Option */}
                <button onClick={handleSelectManual} className="w-full text-left p-4 rounded-xl border-2 border-gray-200 hover:border-gray-300 transition-all group" data-id="element-629">
                  <div className="flex items-start gap-4" data-id="element-630">
                    <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center flex-shrink-0" data-id="element-631">
                      <MousePointer size={24} className="text-gray-500" data-id="element-632" />
                    </div>
                    <div className="flex-1" data-id="element-633">
                      <span className="text-sm font-semibold text-navy" data-id="element-634">
                        Pianificazione Manuale
                      </span>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-635">
                        Scegli tu giorno, orario e tipo di pulizia passo per
                        passo
                      </p>
                      <div className="flex items-center gap-3 mt-2" data-id="element-636">
                        <span className="text-[10px] text-gray-400 flex items-center gap-1" data-id="element-637">
                          <Calendar size={10} data-id="element-638" />
                          Controllo totale
                        </span>
                        <span className="text-[10px] text-gray-400 flex items-center gap-1" data-id="element-639">
                          <Clock size={10} data-id="element-640" />
                          Una pulizia alla volta
                        </span>
                      </div>
                    </div>
                  </div>
                </button>
              </div>

              {/* Property Info */}
              <div className="bg-gray-50 rounded-lg p-3 flex items-center gap-3 mt-4" data-id="element-641">
                <div className="w-10 h-10 rounded-lg bg-white border border-gray-200 flex items-center justify-center" data-id="element-642">
                  <Home size={18} className="text-gray-400" data-id="element-643" />
                </div>
                <div data-id="element-644">
                  <div className="text-sm font-medium text-navy" data-id="element-645">
                    Villa Sunset
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-646">
                    Proprietà selezionata
                  </div>
                </div>
              </div>
            </div>}

          {/* AI Mode - Step 1: Analysis & Suggestions */}
          {wizardMode === 'ai' && wizardStep === 1 && <div className="space-y-4" data-id="element-647">
              {isAnalyzing ? <div className="text-center py-8" data-id="element-648">
                  <div className="w-16 h-16 rounded-full bg-teal-50 flex items-center justify-center mx-auto mb-4" data-id="element-649">
                    <Sparkles size={28} className="text-teal-deep animate-pulse" data-id="element-650" />
                  </div>
                  <h3 className="text-sm font-semibold text-navy mb-2" data-id="element-651">
                    Analisi in corso...
                  </h3>
                  <p className="text-xs text-gray-500 mb-4" data-id="element-652">
                    Sto analizzando i check-in e check-out per trovare gli slot
                    migliori
                  </p>
                  <div className="w-48 h-1 bg-gray-200 rounded-full mx-auto overflow-hidden" data-id="element-653">
                    <div className="h-full bg-teal-deep rounded-full animate-[loading_1.5s_ease-in-out_infinite]" style={{
                width: '60%'
              }} data-id="element-654" />
                  </div>
                </div> : <>
                  <div data-id="element-655">
                    <div className="flex items-center gap-2 mb-1" data-id="element-656">
                      <Sparkles size={16} className="text-coral" data-id="element-657" />
                      <h3 className="text-sm font-semibold text-navy" data-id="element-658">
                        Slot suggeriti dall'AI
                      </h3>
                    </div>
                    <p className="text-xs text-gray-500" data-id="element-659">
                      Seleziona gli slot che vuoi confermare
                    </p>
                  </div>

                  <div className="space-y-2" data-id="element-660">
                    {aiSuggestions.map(suggestion => <button key={suggestion.id} onClick={() => toggleAiSuggestion(suggestion.id)} className={`w-full text-left p-4 rounded-xl border-2 transition-all ${suggestion.selected ? 'border-teal-deep bg-teal-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-661">
                        <div className="flex items-start gap-3" data-id="element-662">
                          {/* Checkbox */}
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${suggestion.selected ? 'bg-teal-deep border-teal-deep' : 'border-gray-300'}`} data-id="element-663">
                            {suggestion.selected && <Check size={12} className="text-white" data-id="element-664" />}
                          </div>

                          {/* Date badge */}
                          <div className="w-12 h-12 rounded-lg flex flex-col items-center justify-center bg-white border border-gray-200 flex-shrink-0" data-id="element-665">
                            <span className="text-[10px] font-medium text-gray-500 uppercase" data-id="element-666">
                              {suggestion.dayName}
                            </span>
                            <span className="text-lg font-bold leading-tight text-navy" data-id="element-667">
                              {suggestion.dayNumber}
                            </span>
                          </div>

                          {/* Details */}
                          <div className="flex-1 min-w-0" data-id="element-668">
                            <div className="flex items-center gap-2" data-id="element-669">
                              <span className="text-sm font-semibold text-navy" data-id="element-670">
                                {suggestion.time}
                              </span>
                              <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${suggestion.type === 'standard' ? 'bg-teal-100 text-coral' : 'bg-amber-100 text-amber-700'}`} data-id="element-671">
                                {suggestion.type === 'standard' ? 'Standard' : 'Express'}
                              </span>
                            </div>
                            <div className="text-xs text-gray-500 mt-0.5" data-id="element-672">
                              Check-out {suggestion.checkOut} → Check-in{' '}
                              {suggestion.checkIn}
                            </div>
                            <div className="flex items-center gap-1 mt-1" data-id="element-673">
                              <Sparkles size={10} className="text-coral" data-id="element-674" />
                              <span className="text-[10px] text-gray-500" data-id="element-675">
                                {suggestion.reason}
                              </span>
                            </div>
                          </div>

                          {/* Confidence */}
                          <div className="flex items-center gap-1 px-2 py-1 bg-pink-50 rounded-lg flex-shrink-0" data-id="element-676">
                            <span className="text-xs font-bold text-coral" data-id="element-677">
                              {suggestion.confidence}%
                            </span>
                          </div>
                        </div>
                      </button>)}
                  </div>

                  {/* Summary */}
                  {getSelectedAiSuggestions().length > 0 && <div className="bg-gray-50 rounded-xl p-4 space-y-2" data-id="element-678">
                      <div className="flex justify-between text-sm" data-id="element-679">
                        <span className="text-gray-600" data-id="element-680">
                          Pulizie selezionate
                        </span>
                        <span className="font-medium text-navy" data-id="element-681">
                          {getSelectedAiSuggestions().length}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm" data-id="element-682">
                        <span className="text-gray-600" data-id="element-683">Crediti totali</span>
                        <span className="font-bold text-coral" data-id="element-684">
                          {getTotalCredits()} crediti
                        </span>
                      </div>
                    </div>}

                  {/* Credits Warning */}
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2" data-id="element-685">
                    <CreditCard size={16} className="text-amber-600 flex-shrink-0 mt-0.5" data-id="element-686" />
                    <div data-id="element-687">
                      <div className="text-xs font-medium text-amber-800" data-id="element-688">
                        Crediti disponibili: 13
                      </div>
                      <div className="text-xs text-amber-700 mt-0.5" data-id="element-689">
                        Dopo questa prenotazione avrai {13 - getTotalCredits()}{' '}
                        crediti
                      </div>
                    </div>
                  </div>
                </>}
            </div>}

          {/* AI Mode - Step 2: Confirmation */}
          {wizardMode === 'ai' && wizardStep === 2 && <div className="space-y-4" data-id="element-690">
              <div data-id="element-691">
                <h3 className="text-sm font-semibold text-navy mb-1" data-id="element-692">
                  Conferma prenotazioni
                </h3>
                <p className="text-xs text-gray-500" data-id="element-693">
                  Verifica i dettagli prima di confermare
                </p>
              </div>

              <div className="space-y-2" data-id="element-694">
                {getSelectedAiSuggestions().map(suggestion => <div key={suggestion.id} className="p-3 bg-gray-50 rounded-lg flex items-center gap-3" data-id="element-695">
                    <div className="w-10 h-10 rounded-lg flex flex-col items-center justify-center bg-white border border-gray-200" data-id="element-696">
                      <span className="text-[9px] font-medium text-gray-500 uppercase" data-id="element-697">
                        {suggestion.dayName}
                      </span>
                      <span className="text-sm font-bold leading-tight text-navy" data-id="element-698">
                        {suggestion.dayNumber}
                      </span>
                    </div>
                    <div className="flex-1" data-id="element-699">
                      <div className="text-sm font-medium text-navy" data-id="element-700">
                        {suggestion.time}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-701">
                        {suggestion.type === 'standard' ? 'Pulizia Standard' : 'Pulizia Express'}{' '}
                        · 1 credito
                      </div>
                    </div>
                    <CheckCircle size={18} className="text-teal-deep" data-id="element-702" />
                  </div>)}
              </div>

              <div className="bg-gray-50 rounded-xl p-4 space-y-2" data-id="element-703">
                <div className="flex justify-between text-sm" data-id="element-704">
                  <span className="text-gray-600" data-id="element-705">Proprietà</span>
                  <span className="font-medium text-navy" data-id="element-706">Villa Sunset</span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-707">
                  <span className="text-gray-600" data-id="element-708">Pulizie totali</span>
                  <span className="font-medium text-navy" data-id="element-709">
                    {getSelectedAiSuggestions().length}
                  </span>
                </div>
                <div className="pt-2 border-t border-gray-200 flex justify-between text-sm" data-id="element-710">
                  <span className="text-gray-600" data-id="element-711">Crediti da scalare</span>
                  <span className="font-bold text-coral" data-id="element-712">
                    {getTotalCredits()} crediti
                  </span>
                </div>
              </div>
            </div>}

          {/* AI Mode - Step 3: Success */}
          {wizardMode === 'ai' && wizardStep === 3 && <div className="text-center py-6" data-id="element-713">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4" data-id="element-714">
                <CheckCircle size={32} className="text-green-600" data-id="element-715" />
              </div>
              <h3 className="text-lg font-bold text-navy mb-2" data-id="element-716">
                {getSelectedAiSuggestions().length} Pulizie Pianificate!
              </h3>
              <p className="text-sm text-gray-600 mb-6" data-id="element-717">
                Le tue pulizie sono state programmate con successo.
              </p>

              <div className="bg-gray-50 rounded-xl p-4 text-left space-y-2 mb-6" data-id="element-718">
                {getSelectedAiSuggestions().map(s => <div key={s.id} className="flex justify-between text-sm py-1" data-id="element-719">
                    <span className="text-gray-600" data-id="element-720">
                      {s.dayName} {s.dayNumber} {s.month}
                    </span>
                    <span className="font-medium text-navy" data-id="element-721">{s.time}</span>
                  </div>)}
                <div className="pt-2 border-t border-gray-200 flex justify-between text-sm" data-id="element-722">
                  <span className="text-gray-600" data-id="element-723">Crediti scalati</span>
                  <span className="font-bold text-coral" data-id="element-724">
                    -{getTotalCredits()} crediti
                  </span>
                </div>
              </div>

              <div className="text-xs text-gray-500" data-id="element-725">
                Riceverai una notifica di conferma via email.
              </div>
            </div>}

          {/* Manual Mode - Step 1: Select Date */}
          {wizardMode === 'manual' && wizardStep === 1 && <div className="space-y-4" data-id="element-726">
              <div data-id="element-727">
                <h3 className="text-sm font-semibold text-navy mb-1" data-id="element-728">
                  Seleziona il giorno
                </h3>
                <p className="text-xs text-gray-500" data-id="element-729">
                  Scegli quando vuoi programmare la pulizia
                </p>
              </div>

              <div className="grid grid-cols-7 gap-2" data-id="element-730">
                {availableDates.map(date => <button key={date.date} onClick={() => setSelectedDate(date.date)} className={`flex flex-col items-center justify-center aspect-square rounded-xl border-2 transition-all ${selectedDate === date.date ? 'border-teal-deep bg-teal-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-731">
                    <span className="text-[10px] font-medium text-gray-500 uppercase" data-id="element-732">
                      {date.dayName}
                    </span>
                    <span className={`text-lg font-bold leading-tight ${selectedDate === date.date ? 'text-teal-deep' : 'text-navy'}`} data-id="element-733">
                      {date.dayNumber}
                    </span>
                    <span className="text-[10px] text-gray-400" data-id="element-734">
                      {date.month}
                    </span>
                  </button>)}
              </div>

              <div className="bg-gray-50 rounded-lg p-3 flex items-center gap-3" data-id="element-735">
                <div className="w-10 h-10 rounded-lg bg-white border border-gray-200 flex items-center justify-center" data-id="element-736">
                  <Home size={18} className="text-gray-400" data-id="element-737" />
                </div>
                <div data-id="element-738">
                  <div className="text-sm font-medium text-navy" data-id="element-739">
                    Villa Sunset
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-740">
                    Proprietà selezionata
                  </div>
                </div>
              </div>
            </div>}

          {/* Manual Mode - Step 2: Select Time */}
          {wizardMode === 'manual' && wizardStep === 2 && <div className="space-y-4" data-id="element-741">
              <div data-id="element-742">
                <h3 className="text-sm font-semibold text-navy mb-1" data-id="element-743">
                  Seleziona l'orario
                </h3>
                <p className="text-xs text-gray-500" data-id="element-744">
                  Scegli la fascia oraria per la pulizia
                </p>
              </div>

              <div className="space-y-2" data-id="element-745">
                {availableTimeSlots.map(slot => <button key={slot.id} onClick={() => slot.available && setSelectedTime(slot.time)} disabled={!slot.available} className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all ${!slot.available ? 'border-gray-100 bg-gray-50 cursor-not-allowed' : selectedTime === slot.time ? 'border-teal-deep bg-teal-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-746">
                    <div className="flex items-center gap-3" data-id="element-747">
                      <Clock size={18} className={!slot.available ? 'text-gray-300' : selectedTime === slot.time ? 'text-teal-deep' : 'text-gray-400'} data-id="element-748" />
                      <span className={`text-sm font-medium ${!slot.available ? 'text-gray-400' : selectedTime === slot.time ? 'text-teal-deep' : 'text-navy'}`} data-id="element-749">
                        {slot.time}
                      </span>
                    </div>
                    {!slot.available && <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded" data-id="element-750">
                        Non disponibile
                      </span>}
                    {slot.available && selectedTime === slot.time && <CheckCircle size={18} className="text-teal-deep" data-id="element-751" />}
                  </button>)}
              </div>
            </div>}

          {/* Manual Mode - Step 3: Select Type & Confirm */}
          {wizardMode === 'manual' && wizardStep === 3 && <div className="space-y-4" data-id="element-752">
              <div data-id="element-753">
                <h3 className="text-sm font-semibold text-navy mb-1" data-id="element-754">
                  Tipo di pulizia
                </h3>
                <p className="text-xs text-gray-500" data-id="element-755">
                  Seleziona il tipo di intervento
                </p>
              </div>

              <div className="space-y-2" data-id="element-756">
                {cleaningTypes.map(type => <button key={type.id} onClick={() => setSelectedType(type.id)} className={`w-full text-left p-4 rounded-xl border-2 transition-all ${selectedType === type.id ? 'border-teal-deep bg-teal-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-757">
                    <div className="flex items-start justify-between" data-id="element-758">
                      <div data-id="element-759">
                        <div className={`text-sm font-semibold ${selectedType === type.id ? 'text-teal-deep' : 'text-navy'}`} data-id="element-760">
                          {type.name}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5" data-id="element-761">
                          {type.description}
                        </div>
                        <div className="text-xs text-gray-400 mt-1" data-id="element-762">
                          Durata: {type.duration}
                        </div>
                      </div>
                      <div className={`flex items-center gap-1 px-2 py-1 rounded-lg ${selectedType === type.id ? 'bg-teal-100' : 'bg-gray-100'}`} data-id="element-763">
                        <CreditCard size={12} className={selectedType === type.id ? 'text-teal-deep' : 'text-gray-500'} data-id="element-764" />
                        <span className={`text-xs font-bold ${selectedType === type.id ? 'text-teal-deep' : 'text-gray-600'}`} data-id="element-765">
                          {type.credits} credito{type.credits > 1 ? 'i' : ''}
                        </span>
                      </div>
                    </div>
                  </button>)}
              </div>

              {selectedType && <div className="bg-gray-50 rounded-xl p-4 space-y-3" data-id="element-766">
                  <div className="text-xs text-gray-400 uppercase tracking-wider font-medium" data-id="element-767">
                    Riepilogo
                  </div>
                  <div className="space-y-2" data-id="element-768">
                    <div className="flex justify-between text-sm" data-id="element-769">
                      <span className="text-gray-600" data-id="element-770">Proprietà</span>
                      <span className="font-medium text-navy" data-id="element-771">
                        Villa Sunset
                      </span>
                    </div>
                    <div className="flex justify-between text-sm" data-id="element-772">
                      <span className="text-gray-600" data-id="element-773">Data</span>
                      <span className="font-medium text-navy" data-id="element-774">
                        {getSelectedDateInfo()?.dayName}{' '}
                        {getSelectedDateInfo()?.dayNumber}{' '}
                        {getSelectedDateInfo()?.month}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm" data-id="element-775">
                      <span className="text-gray-600" data-id="element-776">Orario</span>
                      <span className="font-medium text-navy" data-id="element-777">
                        {selectedTime}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm" data-id="element-778">
                      <span className="text-gray-600" data-id="element-779">Tipo</span>
                      <span className="font-medium text-navy" data-id="element-780">
                        {getSelectedTypeInfo()?.name}
                      </span>
                    </div>
                    <div className="pt-2 border-t border-gray-200 flex justify-between text-sm" data-id="element-781">
                      <span className="text-gray-600" data-id="element-782">Crediti da scalare</span>
                      <span className="font-bold text-coral" data-id="element-783">
                        {getSelectedTypeInfo()?.credits} credito
                        {(getSelectedTypeInfo()?.credits || 0) > 1 ? 'i' : ''}
                      </span>
                    </div>
                  </div>
                </div>}

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2" data-id="element-784">
                <CreditCard size={16} className="text-amber-600 flex-shrink-0 mt-0.5" data-id="element-785" />
                <div data-id="element-786">
                  <div className="text-xs font-medium text-amber-800" data-id="element-787">
                    Crediti disponibili: 13
                  </div>
                  <div className="text-xs text-amber-700 mt-0.5" data-id="element-788">
                    Dopo questa prenotazione avrai{' '}
                    {13 - (getSelectedTypeInfo()?.credits || 0)} crediti
                  </div>
                </div>
              </div>
            </div>}

          {/* Manual Mode - Step 4: Success */}
          {wizardMode === 'manual' && wizardStep === 4 && <div className="text-center py-6" data-id="element-789">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4" data-id="element-790">
                <CheckCircle size={32} className="text-green-600" data-id="element-791" />
              </div>
              <h3 className="text-lg font-bold text-navy mb-2" data-id="element-792">
                Pulizia Pianificata!
              </h3>
              <p className="text-sm text-gray-600 mb-6" data-id="element-793">
                La tua pulizia è stata programmata con successo.
              </p>

              <div className="bg-gray-50 rounded-xl p-4 text-left space-y-2 mb-6" data-id="element-794">
                <div className="flex justify-between text-sm" data-id="element-795">
                  <span className="text-gray-600" data-id="element-796">Proprietà</span>
                  <span className="font-medium text-navy" data-id="element-797">Villa Sunset</span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-798">
                  <span className="text-gray-600" data-id="element-799">Data</span>
                  <span className="font-medium text-navy" data-id="element-800">
                    {getSelectedDateInfo()?.dayName}{' '}
                    {getSelectedDateInfo()?.dayNumber}{' '}
                    {getSelectedDateInfo()?.month}
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-801">
                  <span className="text-gray-600" data-id="element-802">Orario</span>
                  <span className="font-medium text-navy" data-id="element-803">{selectedTime}</span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-804">
                  <span className="text-gray-600" data-id="element-805">Tipo</span>
                  <span className="font-medium text-navy" data-id="element-806">
                    {getSelectedTypeInfo()?.name}
                  </span>
                </div>
                <div className="pt-2 border-t border-gray-200 flex justify-between text-sm" data-id="element-807">
                  <span className="text-gray-600" data-id="element-808">Crediti scalati</span>
                  <span className="font-bold text-coral" data-id="element-809">
                    -{getSelectedTypeInfo()?.credits} credito
                    {(getSelectedTypeInfo()?.credits || 0) > 1 ? 'i' : ''}
                  </span>
                </div>
              </div>

              <div className="text-xs text-gray-500" data-id="element-810">
                Riceverai una notifica di conferma via email.
              </div>
            </div>}
        </div>

        {/* Modal Footer */}
        <div className="p-4 lg:p-6 border-t border-gray-200" data-id="element-811">
          {/* Choice mode - no footer */}
          {wizardMode === 'choice' && <div className="text-xs text-center text-gray-400" data-id="element-812">
              Seleziona una modalità per continuare
            </div>}

          {/* AI Mode - Step 1 */}
          {wizardMode === 'ai' && wizardStep === 1 && !isAnalyzing && <button onClick={() => setWizardStep(2)} disabled={getSelectedAiSuggestions().length === 0} className={`w-full px-4 py-3 rounded-xl text-sm font-medium transition-colors min-h-[48px] ${getSelectedAiSuggestions().length > 0 ? 'bg-coral text-white hover:bg-coral-600' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`} data-id="element-813">
              Continua con {getSelectedAiSuggestions().length} pulizie
            </button>}

          {/* AI Mode - Step 2 */}
          {wizardMode === 'ai' && wizardStep === 2 && <button onClick={handleConfirm} disabled={isConfirming} className={`w-full px-4 py-3 rounded-xl text-sm font-medium transition-colors min-h-[48px] flex items-center justify-center gap-2 ${!isConfirming ? 'bg-coral text-white hover:bg-coral-600' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`} data-id="element-814">
              {isConfirming ? <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-815" />
                  Conferma in corso...
                </> : <>
                  <CheckCircle size={18} data-id="element-816" />
                  Conferma e Scala {getTotalCredits()} Crediti
                </>}
            </button>}

          {/* AI Mode - Step 3 (Success) */}
          {wizardMode === 'ai' && wizardStep === 3 && <div className="flex gap-3" data-id="element-817">
              <Link to="/pulizie" onClick={handleClose} className="flex-1 px-4 py-3 bg-gray-100 text-navy rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors min-h-[48px] flex items-center justify-center" data-id="element-818">
                Vai alle Pulizie
              </Link>
              <button onClick={handleClose} className="w-full px-4 py-3 bg-coral text-white rounded-xl text-sm font-medium hover:bg-coral-600 transition-colors min-h-[48px]" data-id="element-819">
                Chiudi
              </button>
            </div>}

          {/* Manual Mode - Step 1 */}
          {wizardMode === 'manual' && wizardStep === 1 && <button onClick={() => setWizardStep(2)} disabled={!selectedDate} className={`w-full px-4 py-3 rounded-xl text-sm font-medium transition-colors min-h-[48px] ${selectedDate ? 'bg-coral text-white hover:bg-coral-600' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`} data-id="element-820">
              Continua
            </button>}

          {/* Manual Mode - Step 2 */}
          {wizardMode === 'manual' && wizardStep === 2 && <button onClick={() => setWizardStep(3)} disabled={!selectedTime} className={`w-full px-4 py-3 rounded-xl text-sm font-medium transition-colors min-h-[48px] ${selectedTime ? 'bg-coral text-white hover:bg-coral-600' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`} data-id="element-821">
              Continua
            </button>}

          {/* Manual Mode - Step 3 */}
          {wizardMode === 'manual' && wizardStep === 3 && <button onClick={handleConfirm} disabled={!selectedType || isConfirming} className={`w-full px-4 py-3 rounded-xl text-sm font-medium transition-colors min-h-[48px] flex items-center justify-center gap-2 ${selectedType && !isConfirming ? 'bg-coral text-white hover:bg-coral-600' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`} data-id="element-822">
              {isConfirming ? <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-823" />
                  Conferma in corso...
                </> : <>
                  <CheckCircle size={18} data-id="element-824" />
                  Conferma e Scala Crediti
                </>}
            </button>}

          {/* Manual Mode - Step 4 (Success) */}
          {wizardMode === 'manual' && wizardStep === 4 && <div className="flex gap-3" data-id="element-825">
              <Link to="/pulizie" onClick={handleClose} className="flex-1 px-4 py-3 bg-gray-100 text-navy rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors min-h-[48px] flex items-center justify-center" data-id="element-826">
                Vai alle Pulizie
              </Link>
              <button onClick={handleClose} className="w-full px-4 py-3 bg-coral text-white rounded-xl text-sm font-medium hover:bg-coral-600 transition-colors min-h-[48px]" data-id="element-827">
                Chiudi
              </button>
            </div>}
        </div>
      </div>
    </div>;
}